import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DuelSummary, TimeControl } from "./api";

/** A clock reserve: "4:59", and tenths under ten seconds, when they start to matter. */
export function formatClock(ms: number): string {
  const safe = Math.max(0, ms);
  if (safe < 10_000) return `0:0${Math.floor(safe / 1000)}.${Math.floor((safe % 1000) / 100)}`;
  const total = Math.ceil(safe / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** "3 days per move", "5 + 3", "Live". */
export function useTimeControlLabel(): (control: TimeControl) => string {
  const { t } = useTranslation();
  return (control) => {
    if (control.kind === "correspondence")
      return t("duels.tc.correspondence", { count: control.days });
    if (control.kind === "clock")
      return t("duels.tc.clock", {
        minutes: Math.round(control.base_s / 60),
        increment: control.increment_s,
      });
    return t("duels.tc.realtime");
  };
}

/** "in 2 days", "in 5 hours": what is left until a correspondence deadline. */
export function useTimeLeft(): (ms: number) => string {
  const { i18n } = useTranslation();
  const format = new Intl.RelativeTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
    numeric: "always",
  });
  return (ms) => {
    const minutes = Math.max(1, Math.round(ms / 60_000));
    if (minutes < 60) return format.format(minutes, "minute");
    const hours = Math.round(minutes / 60);
    if (hours < 48) return format.format(hours, "hour");
    return format.format(Math.round(hours / 24), "day");
  };
}

/** Re-renders every `intervalMs` while `on`, for a clock that ticks. */
export function useNow(intervalMs: number, on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, on]);
  return on ? now : Date.now();
}

/** The result from this player's seat: won, lost or drawn, or the plain result when watching
 * a duel they are not seated at (an open challenge). */
export function outcomeFor(duel: DuelSummary): "won" | "lost" | "draw" | null {
  if (!duel.result) return null;
  if (duel.result === "draw") return "draw";
  if (!duel.my_side) return null;
  return duel.result === duel.my_side ? "won" : "lost";
}

/** The type's words for why a duel ended, falling back on the core's for its own reasons. */
export function useReason(): (ns: string, reason: string | null) => string {
  const { t } = useTranslation();
  return (ns, reason) =>
    reason ? t([`${ns}:reasons.${reason}`, `duels.reasons.${reason}`, "duels.reasons.other"]) : "";
}
